/**
 * API Service for TheAdsFather Phase 2 Frontend
 * Handles all communication with the backend web API
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { AdMetadata, AdRecord, InitiateUploadRequest } from '../types';

// API-specific interfaces
export type MetaPerformanceGoal = 'OFFSITE_CONVERSIONS' | 'VALUE';
export interface GoogleAdsCampaign {
  campaign_id: string;
  name: string;
}

export interface GoogleAdsAdGroup {
  ad_group_id: string;
  name: string;
  resource_name: string;
  app_ad_count: number;
}

export interface GoogleAdsCampaignAdGroupSelection {
  campaign_id: string;
  ad_group_ids: string[];
}

export interface GoogleAdsStoredAssociation {
  campaign_id: string;
  ad_group_id: string;
  ad_group_resource_name: string;
  campaign_name?: string;
  ad_group_name?: string;
  removable?: boolean;
  removal_blocked_reason?: string | null;
}
export interface GoogleAdsAssociationsResponse {
  ad_id: string;
  asset: {
    asset_id: string;
    resource_name: string;
    asset_type: 'YOUTUBE_VIDEO' | 'IMAGE' | 'MEDIA_BUNDLE';
  } | null;
  associations: GoogleAdsStoredAssociation[];
  total: number;
}


type NonEmptyGoogleAdsCampaignAdGroupSelections = [
  GoogleAdsCampaignAdGroupSelection,
  ...GoogleAdsCampaignAdGroupSelection[],
];

export type GoogleAdsAssociationRequest =
  | {
      campaign_ad_groups: NonEmptyGoogleAdsCampaignAdGroupSelections;
      remove_campaign_ad_groups?: NonEmptyGoogleAdsCampaignAdGroupSelections;
    }
  | {
      campaign_ad_groups?: NonEmptyGoogleAdsCampaignAdGroupSelections;
      remove_campaign_ad_groups: NonEmptyGoogleAdsCampaignAdGroupSelections;
    };

export interface GoogleAdsAdGroupFailure {
  campaign_id: string;
  ad_group_id: string;
  error: string;
}

export interface GoogleAdsAssociationResponse {
  status: 'success' | 'partial_success';
  message: string;
  ad_id: string;
  google_ads_asset_id: string;
  asset_type: 'YOUTUBE_VIDEO' | 'IMAGE' | 'MEDIA_BUNDLE';
  successful_ad_groups: Array<{
    campaign_id: string;
    ad_group_id: string;
    ad_group_ad_resource_name: string;
  }>;
  failed_ad_groups: GoogleAdsAdGroupFailure[];
  already_uploaded_ad_groups: Array<{ campaign_id: string; ad_group_id: string }>;
  removed_ad_groups: Array<{
    campaign_id: string;
    ad_group_id: string;
    ad_group_ad_resource_names: string[];
  }>;
  /** Absent on deployments predating idempotent removals; treat as an empty list. */
  already_removed_ad_groups?: Array<{ campaign_id: string; ad_group_id: string }>;
  failed_removed_ad_groups: GoogleAdsAdGroupFailure[];
}

/**
 * Error thrown by Google Ads API methods. Carries the backend's structured
 * detail so callers can identify upload failures and list failed placements.
 */
export interface GoogleAdsUploadError extends Error {
  code?: string;
  failed_ad_groups?: GoogleAdsAdGroupFailure[];
  failed_removed_ad_groups?: GoogleAdsAdGroupFailure[];
}


export interface UploadSession {
  session_id: string;
  presigned_url: string;
  s3_key: string;
  expires_at: string;
  metadata: AdMetadata;
}

export interface AdDetail extends AdRecord {
  youtube_job_id?: string;
  youtube_video_id?: string;
  meta_job_id?: string;
  meta_ad_id?: string;
  google_ads_asset_id?: string;
  disabled: boolean;
}

export interface Production {
  production_id: number;
  production_name: string;
  description?: string;
  created_by: string;
  ad_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  production_performance_7d?: import('../types').PlatformPerformanceBreakdown;
  production_performance_30d?: import('../types').PlatformPerformanceBreakdown;
  production_performance_all?: import('../types').PlatformPerformanceBreakdown;
  production_performance_updated_at?: string;
  has_activity_7d?: boolean;
}

export interface ValidationResult {
  is_unique: boolean;
  production_id: number;
  creative_number: number;
  ratio: string;
  error_message?: string;
  error_code?: string;
}

export interface CreativeNumberSuggestion {
  suggested_creative_number: number;
}

export type GoogleAdsAssetType = 'YOUTUBE_VIDEO' | 'IMAGE' | 'MEDIA_BUNDLE';

export interface GoogleAdsUploadResponse {
  status: string;
  message: string;
  google_ads_asset_id?: string;
  resource_name?: string;
  asset_type?: GoogleAdsAssetType;
}


const toCreativeNumber = (value: unknown, fieldName: string): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    throw new Error(`${fieldName} must be a positive integer`);
  }
  return parsed;
};

const toCreativeNumberList = (value: unknown, fieldName: string): number[] => {
  if (!Array.isArray(value)) {
    throw new Error(`${fieldName} must be an array`);
  }
  return value.map((item, index) => toCreativeNumber(item, `${fieldName}[${index}]`));
};

export const getApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
      return data.message;
    }
    if (typeof error.message === 'string' && error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Unknown error';
};

/**
 * The `{campaign_id, ad_group_id}` core that every placement in an association response carries,
 * whether it succeeded, failed, or was already in the requested state. Nothing else on an entry is
 * read by the UI: the dialog resolves campaign/ad group names and resource names from its own
 * loaded state, so a bucket-specific field like `ad_group_ad_resource_name` rides along unchecked.
 */
interface GoogleAdsPlacement {
  campaign_id: string;
  ad_group_id: string;
}

const toPlacement = (entry: unknown): GoogleAdsPlacement | undefined => {
  if (entry && typeof entry === 'object' && 'campaign_id' in entry && 'ad_group_id' in entry) {
    const { campaign_id: campaignId, ad_group_id: adGroupId } = entry;
    if (typeof campaignId === 'string' && typeof adGroupId === 'string') {
      return { campaign_id: campaignId, ad_group_id: adGroupId };
    }
  }
  return undefined;
};

const toAdGroupFailure = (entry: unknown): GoogleAdsAdGroupFailure | undefined => {
  if (!entry || typeof entry !== 'object' || !('error' in entry)) {
    return undefined;
  }
  const placement = toPlacement(entry);
  const { error: reason } = entry;
  if (!placement || typeof reason !== 'string') {
    return undefined;
  }
  return { ...placement, error: reason };
};

// A failure list is read as the authoritative breakdown of which placements failed, so one
// malformed entry poisons the whole array: keeping the rest would present a partial list as
// complete, and inventing a reason would attribute an error we never received. Reject the array
// and let callers fall back to the top-level message.
const toAdGroupFailures = (value: unknown): GoogleAdsAdGroupFailure[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }
  const entries: unknown[] = value;
  const failures: GoogleAdsAdGroupFailure[] = [];
  for (const entry of entries) {
    const failure = toAdGroupFailure(entry);
    if (!failure) {
      console.error(
        'Discarding a malformed Google Ads ad group failure list; falling back to the top-level message:',
        entries,
      );
      return undefined;
    }
    failures.push(failure);
  }
  return failures;
};

const toGoogleAdsError = (prefix: string, error: unknown): GoogleAdsUploadError => {
  const googleAdsError: GoogleAdsUploadError = new Error(`${prefix}: ${getApiErrorMessage(error)}`);
  const data: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  if (data && typeof data === 'object') {
    if ('code' in data && typeof data.code === 'string') {
      googleAdsError.code = data.code;
    }
    const failedAdGroups = 'failed_ad_groups' in data ? toAdGroupFailures(data.failed_ad_groups) : undefined;
    if (failedAdGroups) {
      googleAdsError.failed_ad_groups = failedAdGroups;
    }
    const failedRemovedAdGroups = 'failed_removed_ad_groups' in data
      ? toAdGroupFailures(data.failed_removed_ad_groups)
      : undefined;
    if (failedRemovedAdGroups) {
      googleAdsError.failed_removed_ad_groups = failedRemovedAdGroups;
    }
  }
  return googleAdsError;
};

// Same stance as `toAdGroupFailures`, applied to the placement buckets: the list is the
// authoritative record of what happened in the advertiser account, so a single unreadable entry
// drops the whole bucket rather than letting the page count a phantom placement.
const toPlacements = <T extends GoogleAdsPlacement>(value: unknown): T[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }
  const entries: unknown[] = value;
  if (entries.some(entry => toPlacement(entry) === undefined)) {
    console.error(
      'Discarding a malformed Google Ads placement list from a resolved association response:',
      entries,
    );
    return [];
  }
  // Only the shared core is validated; each entry keeps its bucket-specific resource-name fields.
  return entries as T[];
};

export interface CreativeMetadataResponse {
  creative_number: number;
  exists: boolean;
  metadata: {
    name_en: string;
    language: string;
    created_by: string;
    watchers: string[];
    duration?: number;
  } | null;
  total_ads_with_number?: number;
}

export interface WatcherSearchResult {
  watchers: Array<{
    id: string;
    name: string;
    display_name: string;
    real_name?: string;
    profile_image?: string;
    is_active: boolean;
  }>;
  query: string;
  total: number;
  message?: string;
}

export interface WatcherProfile {
  id: string;
  display_name: string;
  profile_image?: string | null;
}

export interface License {
  license_id: string;
  license_type: 'song' | 'actor';
  name: string;
  expiry_date?: string | null;
  notes?: string | null;
  status: 'active' | 'expiring_soon' | 'expired' | 'no_expiry';
  days_until_expiry?: number | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
  has_activity_7d?: boolean;
}

export interface LicenseLink {
  license_id: string;
  entity_id: string;
  entity_type: 'ad' | 'production';
  linked_by: string;
  linked_at: string;
}

export interface LicenseAuditEntry {
  license_id: string;
  timestamp: string;
  action: 'created' | 'updated' | 'deleted' | 'linked' | 'unlinked';
  changed_by: string;
  field_changed?: string;
  old_value?: any;
  new_value?: any;
  entity_id?: string;
}

export interface JobRecord {
  job_id: string;
  user_id: string;
  channel_id: string;
  video_url: string;
  platform: string;
  status: string;
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any>;
  platform_response: Record<string, any>;
  production_id?: number;
  creative_number?: number;
  ratio?: string;
  source?: string;
  watchers?: string[];
  last_heartbeat?: string;
}

export interface JobsListResponse {
  jobs: JobRecord[];
  total_count: number;
  filters_applied: {
    status?: string;
    platform?: string;
    user_id?: string;
    source?: string;
    limit: number;
  };
  summary: {
    by_status: Record<string, number>;
    by_platform: Record<string, number>;
    by_source: Record<string, number>;
  };
}

export interface JobsListFilters {
  status?: string;
  platform?: string;
  user_id?: string;
  source?: string;
  limit?: number;
}

// API Configuration: default to relative /api for transparent local/staging/prod
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Create axios instance with default configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to validate JWT token
const isTokenValid = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp > currentTime + 60; // Add 1 minute buffer
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

// Request interceptor for adding auth token and debugging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);

    // Add authentication token if available
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      // Validate token before using it
      if (isTokenValid(authToken)) {
        // Use Bearer token format as expected by the backend
        config.headers.Authorization = `Bearer ${authToken}`;
        console.log('Added valid auth token to request');
      } else {
        console.warn('Auth token is expired or invalid - clearing from storage');
        // Clear invalid tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        // Don't add the token to the request
        console.warn('Request sent without authentication - may fail');
      }
    } else {
      console.warn('No auth token found - request may fail in production');
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    
    // Handle common error cases
    if (error.response?.status === 401) {
      // Handle authentication errors
      console.error('Authentication required - redirecting to login');
      const errorData = error.response?.data;
      if (errorData?.auth_required) {
        // Clear invalid tokens and redirect to login
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.setItem('debug_api_auth_failure', JSON.stringify({
          timestamp: new Date().toISOString(),
          error: errorData,
          url: error.config?.url
        }));
        // Don't auto-reload - let the auth context handle it
        console.warn('🚨 Auth token invalid - clearing storage but not reloading');
      }
    } else if (error.response?.status === 403) {
      // Handle authorization errors
      console.error('Access forbidden');
    } else if (error.response?.status >= 500) {
      // Handle server errors
      console.error('Server error');
    }
    
    return Promise.reject(error);
  }
);

/**
 * API Service Class
 */
export class ApiService {
  /**
   * Upload Management
   */
  
  /**
   * Initiate file upload process
   */
  async initiateUpload(payload: InitiateUploadRequest): Promise<UploadSession> {
    try {
      const response = await apiClient.post<UploadSession>('/upload/initiate', payload);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Complete file upload process
   */
  async completeUpload(sessionId: string, s3Key: string, metadata?: AdMetadata): Promise<AdRecord> {
    try {
      const payload = {
        session_id: sessionId,
        s3_key: s3Key,
        metadata
      };
      
      const response = await apiClient.post<AdRecord>('/upload/complete', payload);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to complete upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Production Management
   */
  
  /**
   * Get list of productions for a user
   */
  async getProductions(createdBy?: string): Promise<Production[]> {
    try {
      const config = createdBy ? { params: { created_by: createdBy } } : undefined;
      const response = await apiClient.get<{ productions: Production[] }>('/productions', config);
      return response.data.productions;
    } catch (error: any) {
      throw new Error(`Failed to get productions: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get a single production by ID
   */
  async getProduction(productionId: number): Promise<Production> {
    try {
      const response = await apiClient.get<Production>(`/productions/${productionId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get production: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get ads for a specific production
   */
  async getProductionAds(productionId: number): Promise<AdRecord[]> {
    try {
      const response = await apiClient.get<{ ads: AdRecord[] }>(`/productions/${productionId}/ads`);
      return response.data.ads;
    } catch (error: any) {
      throw new Error(`Failed to get production ads: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get a single ad by id
   */
  async getAd(adId: string): Promise<AdDetail> {
    try {
      // URL encode the ad ID to handle special characters like +
      const encodedAdId = encodeURIComponent(adId);
      // Use public endpoint if no auth token is present (anonymous access)
      const authToken = localStorage.getItem('auth_token');
      const endpoint = authToken ? `/ads/${encodedAdId}` : `/public/ads/${encodedAdId}`;
      const response = await apiClient.get<AdDetail>(endpoint);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get ad: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Initiate YouTube upload for an existing ad
   */
  async uploadAdToYouTube(adId: string): Promise<{job_id: string; status: string; message: string}> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.post<{job_id: string; status: string; message: string}>(`/ads/${encodedAdId}/upload-youtube`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate YouTube upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Initiate Meta Ad Library upload for an existing ad
   */
  async uploadAdToMeta(adId: string, accountKey: string = 'default'): Promise<{job_id: string; status: string; message: string}> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.post<{job_id: string; status: string; message: string}>(`/ads/${encodedAdId}/upload-meta`, { account_key: accountKey });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate Meta upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Initiate AppLovin upload for an existing ad
   */
  async uploadAdToApplovin(
    adId: string,
    templateCreativeSetId: string,
    campaignId: string,
  ): Promise<{job_id: string; status: string; message: string}> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.post<{job_id: string; status: string; message: string}>(
        `/ads/${encodedAdId}/upload-applovin`,
        { template_creative_set_id: templateCreativeSetId, campaign_id: campaignId },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate AppLovin upload: ${error.response?.data?.message || error.message}`);
    }
  }

  async getGoogleAdsCampaigns(): Promise<{ campaigns: GoogleAdsCampaign[]; total: number }> {
    try {
      const response = await apiClient.get<{ campaigns: GoogleAdsCampaign[]; total: number }>('/google-ads/campaigns');
      return response.data;
    } catch (error: unknown) {
      throw toGoogleAdsError('Failed to load Google Ads campaigns', error);
    }
  }

  async getGoogleAdsAdGroups(campaignId: string): Promise<{
    campaign_id: string;
    ad_groups: GoogleAdsAdGroup[];
    total: number;
  }> {
    try {
      const response = await apiClient.get<{
        campaign_id: string;
        ad_groups: GoogleAdsAdGroup[];
        total: number;
      }>(`/google-ads/campaigns/${encodeURIComponent(campaignId)}/ad-groups`);
      return response.data;
    } catch (error: unknown) {
      throw toGoogleAdsError('Failed to load Google Ads ad groups', error);
    }
  }

  async getGoogleAdsAssociations(adId: string): Promise<GoogleAdsAssociationsResponse> {
    try {
      const response = await apiClient.get<GoogleAdsAssociationsResponse>(
        `/ads/${encodeURIComponent(adId)}/google-ads-associations`,
      );
      return response.data;
    } catch (error: unknown) {
      throw toGoogleAdsError('Failed to load Google Ads associations', error);
    }
  }

  /**
   * Attaches and/or detaches the ad's Google Ads asset, returning what the backend reports as live.
   *
   * The resolved body is only *claimed* to be a `GoogleAdsAssociationResponse` by the transport, and
   * every list on it is rendered as fact — the dialog spreads the attached/detached buckets and
   * reads `.length` on the failure lists, and the ad detail page counts each bucket for its summary
   * — so each one is normalized to an array before it reaches a caller:
   *
   * - Absent or empty means "nothing in this bucket", a legitimate result (a pure removal reports
   *   no successes, and deployments predating idempotent removals never sent
   *   `already_removed_ad_groups` at all). It becomes `[]` silently.
   * - Present but not matching the contract is a breach we refuse to render: `Ad group undefined`,
   *   or a counted-but-unreadable placement, would state something about the advertiser account we
   *   never received. The bucket is dropped, logged once, and the operator keeps `status`/`message`.
   *
   * A bucket present in a *non-list* shape carries no detail at all, exactly as the error path
   * treats it, so the two directions cannot disagree about what counts as malformed.
   *
   * Before normalizing buckets, require an object with a recognized outcome status and a string
   * message when supplied. An invalid envelope cannot establish success, and an object-valued
   * message would crash React when the dialog renders it.
   */
  async associateGoogleAdsAsset(
    adId: string,
    request: GoogleAdsAssociationRequest,
  ): Promise<GoogleAdsAssociationResponse> {
    try {
      const response = await apiClient.post<GoogleAdsAssociationResponse>(
        `/ads/${encodeURIComponent(adId)}/google-ads-associations`,
        request,
      );
      const body = response.data;
      if (
        !body
        || typeof body !== 'object'
        || Array.isArray(body)
        || (body.status !== 'success' && body.status !== 'partial_success')
        || ('message' in body && typeof body.message !== 'string')
      ) {
        throw new Error('Invalid Google Ads association response');
      }
      return {
        ...body,
        successful_ad_groups: toPlacements(body.successful_ad_groups),
        failed_ad_groups: toAdGroupFailures(body.failed_ad_groups) ?? [],
        already_uploaded_ad_groups: toPlacements(body.already_uploaded_ad_groups),
        removed_ad_groups: toPlacements(body.removed_ad_groups),
        // Stays optional on the interface because older deployments and test fixtures omit it, but
        // a normalized response always carries an array so no caller needs its own `?? []`.
        already_removed_ad_groups: toPlacements(body.already_removed_ad_groups),
        failed_removed_ad_groups: toAdGroupFailures(body.failed_removed_ad_groups) ?? [],
      };
    } catch (error: unknown) {
      throw toGoogleAdsError('Failed to create Google Ads associations', error);
    }
  }

  /**
   * Upload a persisted creative to the Google Ads asset library.
   */
  async addToGoogleAds(adId: string): Promise<GoogleAdsUploadResponse> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.post<GoogleAdsUploadResponse>(`/ads/${encodedAdId}/add-to-google-ads`);
      return response.data;
    } catch (error: unknown) {
      throw toGoogleAdsError('Failed to add to Google Ads', error);
    }
  }

  /**
   * Initiate batch YouTube upload for multiple ads
   */
  async batchUploadToYouTube(adIds: string[]): Promise<{
    message: string;
    successful_jobs: Array<{ad_id: string; job_id: string}>;
    failed_jobs: Array<{ad_id: string; error: string}>;
    total_ads: number;
    successful_count: number;
    failed_count: number;
  }> {
    try {
      const response = await apiClient.post<{
        message: string;
        successful_jobs: Array<{ad_id: string; job_id: string}>;
        failed_jobs: Array<{ad_id: string; error: string}>;
        total_ads: number;
        successful_count: number;
        failed_count: number;
      }>('/ads/batch-upload-youtube', { ad_ids: adIds });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate batch YouTube upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Initiate batch Meta Ad Library upload for multiple ads
   */
  async batchUploadToMeta(adIds: string[], accountKey: string = 'default'): Promise<{
    message: string;
    successful_jobs: Array<{ad_id: string; job_id: string}>;
    failed_jobs: Array<{ad_id: string; error: string}>;
    total_ads: number;
    successful_count: number;
    failed_count: number;
  }> {
    try {
      const response = await apiClient.post<{
        message: string;
        successful_jobs: Array<{ad_id: string; job_id: string}>;
        failed_jobs: Array<{ad_id: string; error: string}>;
        total_ads: number;
        successful_count: number;
        failed_count: number;
      }>('/ads/batch-upload-meta', { ad_ids: adIds, account_key: accountKey });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate batch Meta upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Initiate batch AppLovin upload for multiple ads
   */
  async batchUploadToApplovin(
    adIds: string[],
    templateCreativeSetId: string,
    campaignId: string,
  ): Promise<{
    message: string;
    successful_jobs: Array<{ad_id: string; job_id: string}>;
    failed_jobs: Array<{ad_id: string; error: string}>;
    total_ads: number;
    successful_count: number;
    failed_count: number;
  }> {
    try {
      const response = await apiClient.post<{
        message: string;
        successful_jobs: Array<{ad_id: string; job_id: string}>;
        failed_jobs: Array<{ad_id: string; error: string}>;
        total_ads: number;
        successful_count: number;
        failed_count: number;
      }>('/ads/batch-upload-applovin', {
        ad_ids: adIds,
        template_creative_set_id: templateCreativeSetId,
        campaign_id: campaignId,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to initiate batch AppLovin upload: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Create a zip file with multiple video files for batch download
   */
  async batchDownloadVideos(adIds: string[]): Promise<{
    message: string;
    download_url: string;
    zip_size: number;
    successful_count: number;
    failed_count: number;
    failed_ads: Array<{ad_id: string; error: string}>;
    expires_in: string;
  }> {
    try {
      const response = await apiClient.post<{
        message: string;
        download_url: string;
        zip_size: number;
        successful_count: number;
        failed_count: number;
        failed_ads: Array<{ad_id: string; error: string}>;
        expires_in: string;
      }>('/ads/batch-download', { ad_ids: adIds });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to create batch download: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get all jobs for a specific ad
   */
  async getAdJobs(adId: string, limit: number = 50): Promise<{jobs: any[], count: number, ad_id: string}> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.get<{jobs: any[], count: number, ad_id: string}>(`/ads/${encodedAdId}/jobs`, {
        params: { limit }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get ad jobs: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get status of a specific job
   */
  async getJobStatus(jobId: string): Promise<JobRecord> {
    try {
      const response = await apiClient.get<JobRecord>(`/jobs/${jobId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get job status: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Update ad metadata
   */
  async updateAdMetadata(adId: string, metadata: Partial<AdMetadata>): Promise<AdDetail> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.put<AdDetail>(`/ads/${encodedAdId}/metadata`, metadata);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to update ad metadata: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get list of all jobs with optional filtering
   */
  async getAllJobs(filters?: JobsListFilters): Promise<JobsListResponse> {
    try {
      const params: Record<string, string> = {};
      
      if (filters?.status) params.status = filters.status;
      if (filters?.platform) params.platform = filters.platform;
      if (filters?.user_id) params.user_id = filters.user_id;
      if (filters?.source) params.source = filters.source;
      if (filters?.limit) params.limit = filters.limit.toString();
      
      const response = await apiClient.get<JobsListResponse>('/jobs', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get jobs: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Validation Services
   */
  
  /**
   * Validate identifier uniqueness
   */
  async validateIdentifier(productionId: number, creativeNumber: number, ratio: string): Promise<ValidationResult> {
    try {
      const payload = {
        production_id: productionId,
        creative_number: creativeNumber,
        ratio: ratio
      };
      
      const response = await apiClient.post<ValidationResult>('/validate/identifier', payload);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to validate identifier: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get suggested creative number
   */
  async getSuggestedCreativeNumber(): Promise<number> {
    try {
      const response = await apiClient.get<{ suggested_creative_number: unknown }>('/suggest/creative-number');
      return toCreativeNumber(response.data.suggested_creative_number, 'suggested_creative_number');
    } catch (error: unknown) {
      throw new Error(`Failed to get suggested creative number: ${getApiErrorMessage(error)}`);
    }
  }

  /**
   * Reserve the next creative number (actually consumes it)
   */
  async getReservedCreativeNumber(): Promise<number> {
    try {
      const response = await apiClient.post<{ reserved_creative_number: unknown }>('/reserve/creative-number');
      return toCreativeNumber(response.data.reserved_creative_number, 'reserved_creative_number');
    } catch (error: unknown) {
      throw new Error(`Failed to reserve creative number: ${getApiErrorMessage(error)}`);
    }
  }

  /**
   * Reserve multiple creative numbers for auto-assignment
   */
  async getReservedCreativeNumbers(count: number): Promise<number[]> {
    try {
      return await this.getSuggestedCreativeNumbers(count);
    } catch (error: unknown) {
      throw new Error(`Failed to reserve creative numbers: ${getApiErrorMessage(error)}`);
    }
  }

  /**
   * Get existing metadata for a creative number
   */
  async getCreativeMetadata(creativeNumber: number): Promise<CreativeMetadataResponse> {
    try {
      console.log('[API] Fetching creative metadata for:', creativeNumber);
      const response = await apiClient.get<CreativeMetadataResponse>('/creative/metadata', {
        params: { creative_number: creativeNumber }
      });
      console.log('[API] Creative metadata response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[API] Error fetching creative metadata:', error);
      throw new Error(`Failed to get creative metadata: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get bulk suggested creative numbers
   */
  async getSuggestedCreativeNumbers(count: number): Promise<number[]> {
    try {
      const response = await apiClient.get<{ suggested_creative_numbers: unknown }>('/suggest/creative-numbers', {
        params: { count }
      });
      return toCreativeNumberList(response.data.suggested_creative_numbers, 'suggested_creative_numbers');
    } catch (error: unknown) {
      throw new Error(`Failed to get suggested creative numbers: ${getApiErrorMessage(error)}`);
    }
  }

  /**
   * Watcher Services
   */
  
  /**
   * Search for watchers (Slack users)
   */
  async searchWatchers(query: string): Promise<WatcherSearchResult> {
    try {
      const response = await apiClient.get<WatcherSearchResult>('/watchers/search', {
        params: { q: query }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to search watchers: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Notify watchers about an event
   */
  async notifyWatchers(adId: string, watchers: string[], eventType: string): Promise<void> {
    try {
      const payload = {
        ad_id: adId,
        watchers: watchers,
        event_type: eventType
      };
      
      await apiClient.post('/watchers/notify', payload);
    } catch (error: any) {
      throw new Error(`Failed to notify watchers: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Resolve slack IDs to basic profile info
   */
  async resolveWatchers(ids: string[]): Promise<WatcherProfile[]> {
    try {
      const response = await apiClient.post<{ watchers: WatcherProfile[] }>(
        '/watchers/resolve',
        { ids }
      );
      return response.data.watchers;
    } catch (error: any) {
      throw new Error(`Failed to resolve watchers: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Utility Methods
   */
  
  /**
   * Check API health
   */
  async healthCheck(): Promise<boolean> {
    try {
      await apiClient.get('/health');
      return true;
    } catch (error) {
      console.warn('API health check failed:', error);
      return false;
    }
  }

  /**
   * Test API connection
   */
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      await apiClient.get('/health');
      return {
        success: true,
        message: 'API connection successful'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Connection failed'
      };
    }
  }

  /**
   * Get all ads with tag information
   * @param limit - Maximum number of ads to return. Use a very high number (10000+) to get all ads.
   * @param includeDisabled - Whether to include disabled ads. Defaults to false.
   */
  async getAllAds(limit: number = 10000, includeDisabled: boolean = false): Promise<AdRecord[]> {
    try {
      const response = await apiClient.get<{ ads: AdRecord[]; total_count: number }>('/ads', {
        params: { limit, include_disabled: includeDisabled }
      });
      return response.data.ads;
    } catch (error: any) {
      throw new Error(`Failed to get all ads: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get a page of ads with server-side filtering and pagination.
   */
  async getAdsPage(params: {
    page_size?: number;
    cursor?: string | null;
    production_id?: number | null;
    creative_number?: number | null;
    created_by?: string | null;
    upload_status?: string | null;
    meant_to?: string[] | null;
    media_type?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    include_disabled?: boolean;
  }): Promise<{ ads: AdRecord[]; next_cursor: string | null; total_count: number }> {
    try {
      const queryParams: Record<string, string> = {};
      if (params.page_size) queryParams.page_size = String(params.page_size);
      if (params.cursor) queryParams.cursor = params.cursor;
      if (params.production_id != null) queryParams.production_id = String(params.production_id);
      if (params.creative_number != null) queryParams.creative_number = String(params.creative_number);
      if (params.created_by) queryParams.created_by = params.created_by;
      if (params.upload_status) queryParams.upload_status = params.upload_status;
      if (params.meant_to && params.meant_to.length > 0) queryParams.meant_to = params.meant_to.join(',');
      if (params.media_type) queryParams.media_type = params.media_type;
      if (params.date_from) queryParams.date_from = params.date_from;
      if (params.date_to) queryParams.date_to = params.date_to;
      if (params.include_disabled) queryParams.include_disabled = 'true';

      const response = await apiClient.get<{
        ads: AdRecord[];
        next_cursor: string | null;
        total_count: number;
      }>('/ads/paginated', { params: queryParams });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get ads page: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get available filter options, cascading based on currently active filters.
   */
  async getAdsFilterOptions(params: {
    production_id?: number | null;
    creative_number?: number | null;
    created_by?: string | null;
    upload_status?: string | null;
    meant_to?: string[] | null;
    media_type?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    include_disabled?: boolean;
  }): Promise<{
    productions: number[];
    creatives: number[];
    created_by: string[];
    upload_statuses: string[];
    meant_to: string[];
    media_types: string[];
    date_range: { min: string; max: string } | null;
  }> {
    try {
      const queryParams: Record<string, string> = {};
      if (params.production_id != null) queryParams.production_id = String(params.production_id);
      if (params.creative_number != null) queryParams.creative_number = String(params.creative_number);
      if (params.created_by) queryParams.created_by = params.created_by;
      if (params.upload_status) queryParams.upload_status = params.upload_status;
      if (params.meant_to && params.meant_to.length > 0) queryParams.meant_to = params.meant_to.join(',');
      if (params.media_type) queryParams.media_type = params.media_type;
      if (params.date_from) queryParams.date_from = params.date_from;
      if (params.date_to) queryParams.date_to = params.date_to;
      if (params.include_disabled) queryParams.include_disabled = 'true';

      const response = await apiClient.get('/ads/filter-options', { params: queryParams });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get filter options: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Toggle ad disabled status
   * @param adId - The ID of the ad to toggle
   * @param disabled - Whether to disable or enable the ad
   */
  async toggleAdDisabled(adId: string, disabled: boolean): Promise<{ message: string; disabled: boolean }> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.put(`/ads/${encodedAdId}/disabled`, { disabled });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to toggle ad disabled status: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Generate USPs for a specific ad with existing transcription
   */
  async generateUspsForAd(adId: string): Promise<{ message: string; usps?: string[]; generated_at?: string; error?: string }> {
    try {
      const encodedAdId = encodeURIComponent(adId);
      const response = await apiClient.post(`/ads/${encodedAdId}/generate-usps`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to generate USPs for ad ${adId}: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get all ads that use a specific licensed song
   */
  async getAdsByLicensedSong(songName: string): Promise<{ song_name: string; count: number; ads: AdRecord[] }>{
    try {
      const encoded = encodeURIComponent(songName);
      const response = await apiClient.get(`/licensed-songs/${encoded}/ads`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get ads for song '${songName}': ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get all unique licensed songs from existing ads and the licensed songs table
   */
  async getUniqueLicensedSongs(): Promise<string[]> {
    try {
      const [ads, licensedSongsData] = await Promise.allSettled([
        this.getAllAds(1000),
        this.getLicensedSongs()
      ]);

      const songs = new Set<string>();

      if (ads.status === 'fulfilled') {
        ads.value.forEach(ad => {
          const licensedSong = ad.tags?.licensedSong;
          if (licensedSong) {
            if (Array.isArray(licensedSong)) {
              licensedSong.forEach(song => {
                if (song && typeof song === 'string' && song.trim()) {
                  songs.add(song.trim());
                }
              });
            } else if (typeof licensedSong === 'string') {
              const song = licensedSong.trim();
              if (song) {
                songs.add(song);
              }
            }
          }
        });
      }

      if (licensedSongsData.status === 'fulfilled') {
        licensedSongsData.value.songs.forEach(song => {
          if (song.song_name && song.song_name.trim()) {
            songs.add(song.song_name.trim());
          }
        });
      }

      return Array.from(songs).sort();
    } catch (error: any) {
      console.warn('Failed to get unique licensed songs:', error);
      return [];
    }
  }

  /**
   * Get all licensed songs with their expiry information
   */
  async getLicensedSongs(): Promise<{
    songs: Array<{
      song_name: string;
      expiry_date?: string;
      status: 'expired' | 'expiring_soon' | 'active' | 'no_expiry';
      days_until_expiry?: number;
      usage_count: number;
      notes?: string;
      created_at: string;
      updated_at: string;
    }>;
    summary: {
      total_songs: number;
      expired: number;
      expiring_soon: number;
      no_expiry_date: number;
      active: number;
    };
  }> {
    try {
      const response = await apiClient.get('/licensed-songs');
      return response.data;
    } catch (error: any) {
      console.error('Failed to get licensed songs:', error);
      throw new Error(`Failed to get licensed songs: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Sync licensed songs from ads database
   */
  async syncLicensedSongs(): Promise<{
    message: string;
    result: {
      created: number;
      updated: number;
    };
  }> {
    try {
      const response = await apiClient.post('/licensed-songs/sync');
      return response.data;
    } catch (error: any) {
      console.error('Failed to sync licensed songs:', error);
      throw new Error(`Failed to sync licensed songs: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Update licensed song expiry date and notes
   */
  async updateLicensedSongExpiry(
    songName: string,
    data: {
      expiry_date: string | null;
      notes?: string | null;
    }
  ): Promise<{
    message: string;
    song: {
      song_name: string;
      expiry_date?: string;
      status: string;
      days_until_expiry?: number;
      usage_count: number;
      notes?: string;
      created_at: string;
      updated_at: string;
    };
  }> {
    try {
      const encodedSongName = encodeURIComponent(songName);
      const response = await apiClient.put(`/licensed-songs/${encodedSongName}/expiry`, data);
      return response.data;
    } catch (error: any) {
      console.error('Failed to update licensed song:', error);
      throw new Error(`Failed to update licensed song: ${error.response?.data?.message || error.message}`);
    }
  }

  // ============ Meta Ad Creator API ============

  /**
   * Get list of available Meta ad accounts
   */
  async getMetaAccounts(): Promise<{
    accounts: Array<{ key: string; label: string; ad_account_id: string }>;
    total: number;
  }> {
    try {
      const response = await apiClient.get('/meta/accounts');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch Meta accounts: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get list of campaigns from Meta Ads Manager
   */
  async getMetaCampaigns(limit: number = 50, accountKey: string = 'default'): Promise<{
    campaigns: Array<{ id: string; name: string; status: string; objective: string }>;
    total: number;
  }> {
    try {
      const response = await apiClient.get('/meta/campaigns', { params: { limit, account_key: accountKey } });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch Meta campaigns: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get list of ad sets for a specific campaign
   */
  async getMetaAdsets(campaignId: string, limit: number = 50, accountKey: string = 'default'): Promise<{
    adsets: Array<{ id: string; name: string; status: string; campaign_id: string }>;
    campaign_id: string;
    total: number;
  }> {
    try {
      const response = await apiClient.get('/meta/adsets', { params: { campaign_id: campaignId, limit, account_key: accountKey } });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch Meta ad sets: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get available primary text options for ads
   */
  async getMetaPrimaryTextOptions(): Promise<{
    options: Array<{ id: string; text: string }>;
    grouped_options: Record<string, {
      name: string;
      options: Array<{ id: string; text: string; flow: string }>;
    }>;
    all_options: Array<{ id: string; text: string; flow: string }>;
    default_website_url: string;
    default_headline: string;
  }> {
    try {
      const response = await apiClient.get('/meta/primary-text-options');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch primary text options: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get available ad flows configuration
   */
  async getMetaFlows(): Promise<{
    flows: Array<{
      id: string;
      name: string;
      facebook_page_id: string;
      instagram_account_id: string;
      url: string;
      primary_texts: Array<{ id: string; text: string }>;
    }>;
    default_flow: string;
  }> {
    try {
      const response = await apiClient.get('/meta/flows');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch flows: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Create Meta ads from selected assets
   */
  async createMetaAd(params: {
    ad_ids: string[];
    campaign_id: string;
    adset_id: string;
    website_url?: string;
    primary_text?: string;
    headline?: string;
    page_id?: string;
    instagram_id?: string;
    account_key?: string;
  }): Promise<{
    message: string;
    successful_ads: Array<{
      ad_id: string;
      ad_name: string;
      creative_id: string;
      asset_count: number;
      source_ad_ids: string[];
    }>;
    failed_ads: Array<{
      ad_name: string;
      error: string;
      source_ad_ids: string[];
    }>;
    summary: {
      total_assets: number;
      ads_created: number;
      ads_failed: number;
      campaign_id: string;
      adset_id: string;
    };
  }> {
    try {
      const response = await apiClient.post('/meta/create-ad', params);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to create Meta ads: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get available conversion events for ad set creation
   */
  async getMetaConversionEvents(): Promise<{
    events: string[];
    default: string;
  }> {
    try {
      const response = await apiClient.get('/meta/conversion-events');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch conversion events: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get available location options for ad set creation
   */
  async getMetaLocations(): Promise<{
    priority: Array<{ name: string; code: string }>;
    other: Array<{ name: string; code: string }>;
    default: string[];
  }> {
    try {
      const response = await apiClient.get('/meta/locations');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch locations: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Get default campaign name with current Israel date
   */
  async getMetaDefaultCampaignName(): Promise<{
    default_name: string;
    israel_date: string;
  }> {
    try {
      const response = await apiClient.get('/meta/default-campaign-name');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to fetch default campaign name: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Preview ad set name generated from campaign name
   */
  async previewMetaAdsetName(campaignName: string): Promise<{
    campaign_name: string;
    adset_name: string;
  }> {
    try {
      const response = await apiClient.post('/meta/preview-adset-name', { campaign_name: campaignName });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to preview ad set name: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Create a new campaign in Meta Ads Manager
   */
  async createMetaCampaign(params: {
    name: string;
    objective?: string;
    account_key?: string;
  }): Promise<{
    success: boolean;
    campaign: {
      id: string;
      name: string;
      objective: string;
      status: string;
    };
  }> {
    try {
      const response = await apiClient.post('/meta/campaigns', params);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to create campaign: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Create a new ad set in Meta Ads Manager
   */
  async createMetaAdset(params: {
    campaign_id: string;
    name: string;
    daily_budget?: number;
    billing_event?: string;
    optimization_goal?: MetaPerformanceGoal;
    conversion_event?: string;
    locations?: string[];
    attribution_model?: 'standard' | 'incremental';
    account_key?: string;
  }): Promise<{
    success: boolean;
    adset: {
      id: string;
      name: string;
      campaign_id: string;
      daily_budget: number;
      billing_event: string;
      optimization_goal: MetaPerformanceGoal;
      conversion_event: string;
      status: string;
      locations: string[];
      attribution_model?: string;
    };
  }> {
    try {
      const response = await apiClient.post('/meta/adsets', params);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to create ad set: ${error.response?.data?.message || error.message}`);
    }
  }

  // ============ Licensing API (new system) ============

  async getLicenses(type?: 'song' | 'actor'): Promise<{ licenses: License[]; count: number }> {
    try {
      const params: Record<string, string> = {};
      if (type) params.type = type;
      const response = await apiClient.get('/licenses', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get licenses: ${error.response?.data?.message || error.message}`);
    }
  }

  async createLicense(data: {
    license_type: 'song' | 'actor';
    name: string;
    expiry_date?: string | null;
    notes?: string | null;
  }): Promise<{ license: License }> {
    try {
      const response = await apiClient.post('/licenses', data);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to create license: ${error.response?.data?.message || error.message}`);
    }
  }

  async getLicense(licenseId: string): Promise<{ license: License }> {
    try {
      const response = await apiClient.get(`/licenses/${licenseId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get license: ${error.response?.data?.message || error.message}`);
    }
  }

  async updateLicense(licenseId: string, data: {
    name?: string;
    expiry_date?: string | null;
    notes?: string | null;
    clear_notes?: boolean;
  }): Promise<{ license: License }> {
    try {
      const response = await apiClient.put(`/licenses/${licenseId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to update license: ${error.response?.data?.message || error.message}`);
    }
  }

  async deleteLicense(licenseId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete(`/licenses/${licenseId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to delete license: ${error.response?.data?.message || error.message}`);
    }
  }

  async getExpiringLicenses(): Promise<{ licenses: License[]; count: number }> {
    try {
      const response = await apiClient.get('/licenses/expiring');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get expiring licenses: ${error.response?.data?.message || error.message}`);
    }
  }

  async getLicenseEntities(licenseId: string): Promise<{ entities: LicenseLink[]; count: number }> {
    try {
      const response = await apiClient.get(`/licenses/${licenseId}/entities`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get license entities: ${error.response?.data?.message || error.message}`);
    }
  }

  async linkLicense(licenseId: string, entityType: 'ad' | 'production', entityId: string): Promise<{ link: LicenseLink }> {
    try {
      const response = await apiClient.post(`/licenses/${licenseId}/link`, {
        entity_type: entityType,
        entity_id: entityId,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to link license: ${error.response?.data?.message || error.message}`);
    }
  }

  async unlinkLicense(licenseId: string, entityId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete(`/licenses/${licenseId}/link/${entityId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to unlink license: ${error.response?.data?.message || error.message}`);
    }
  }

  async getLicenseAudit(licenseId: string): Promise<{ audit: LicenseAuditEntry[]; count: number }> {
    try {
      const response = await apiClient.get(`/licenses/${licenseId}/audit`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get license audit: ${error.response?.data?.message || error.message}`);
    }
  }

  async getAdLicenses(adId: string): Promise<{ licenses: License[]; count: number }> {
    try {
      const response = await apiClient.get(`/ads/${encodeURIComponent(adId)}/licenses`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get ad licenses: ${error.response?.data?.message || error.message}`);
    }
  }

  async getProductionLicenses(productionId: string | number): Promise<{ licenses: License[]; count: number }> {
    try {
      const response = await apiClient.get(`/productions/${productionId}/licenses`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get production licenses: ${error.response?.data?.message || error.message}`);
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Export individual functions for convenience
export const uploadAPI = {
  initiate: (payload: InitiateUploadRequest) => apiService.initiateUpload(payload),
  complete: (sessionId: string, s3Key: string, metadata?: AdMetadata) => apiService.completeUpload(sessionId, s3Key, metadata),
};

export const productionsAPI = {
  list: (createdBy?: string) => apiService.getProductions(createdBy),
  get: (productionId: number) => apiService.getProduction(productionId),
  getAds: (productionId: number) => apiService.getProductionAds(productionId),
};

export const validationAPI = {
  validateIdentifier: (productionId: number, creativeNumber: number, ratio: string) => 
    apiService.validateIdentifier(productionId, creativeNumber, ratio),
  getSuggestedCreativeNumber: () => apiService.getSuggestedCreativeNumber(),
  getCreativeMetadata: (creativeNumber: number) => apiService.getCreativeMetadata(creativeNumber),
};

export const watchersAPI = {
  search: (query: string) => apiService.searchWatchers(query),
  notify: (adId: string, watchers: string[], eventType: string) => apiService.notifyWatchers(adId, watchers, eventType),
  resolve: (ids: string[]) => apiService.resolveWatchers(ids),
};

export const jobsAPI = {
  getAll: (filters?: JobsListFilters) => apiService.getAllJobs(filters),
  getStatus: (jobId: string) => apiService.getJobStatus(jobId),
};

export const licensedSongsAPI = {
  list: () => apiService.getLicensedSongs(),
  sync: () => apiService.syncLicensedSongs(),
  updateExpiry: (songName: string, data: { expiry_date: string | null; notes?: string | null; }) =>
    apiService.updateLicensedSongExpiry(songName, data),
};

export const licensingAPI = {
  list: (type?: 'song' | 'actor') => apiService.getLicenses(type),
  create: (data: Parameters<typeof apiService.createLicense>[0]) => apiService.createLicense(data),
  get: (id: string) => apiService.getLicense(id),
  update: (id: string, data: Parameters<typeof apiService.updateLicense>[1]) => apiService.updateLicense(id, data),
  delete: (id: string) => apiService.deleteLicense(id),
  getExpiring: () => apiService.getExpiringLicenses(),
  getEntities: (id: string) => apiService.getLicenseEntities(id),
  link: (id: string, entityType: 'ad' | 'production', entityId: string) => apiService.linkLicense(id, entityType, entityId),
  unlink: (id: string, entityId: string) => apiService.unlinkLicense(id, entityId),
  getAudit: (id: string) => apiService.getLicenseAudit(id),
  getAdLicenses: (adId: string) => apiService.getAdLicenses(adId),
  getProductionLicenses: (prodId: string | number) => apiService.getProductionLicenses(prodId),
};

export const metaAPI = {
  getCampaigns: (limit?: number) => apiService.getMetaCampaigns(limit),
  getAdsets: (campaignId: string, limit?: number) => apiService.getMetaAdsets(campaignId, limit),
  getPrimaryTextOptions: () => apiService.getMetaPrimaryTextOptions(),
  getFlows: () => apiService.getMetaFlows(),
  createAd: (params: Parameters<typeof apiService.createMetaAd>[0]) => apiService.createMetaAd(params),
  getConversionEvents: () => apiService.getMetaConversionEvents(),
  getLocations: () => apiService.getMetaLocations(),
  getDefaultCampaignName: () => apiService.getMetaDefaultCampaignName(),
  previewAdsetName: (campaignName: string) => apiService.previewMetaAdsetName(campaignName),
  createCampaign: (params: Parameters<typeof apiService.createMetaCampaign>[0]) => apiService.createMetaCampaign(params),
  createAdset: (params: Parameters<typeof apiService.createMetaAdset>[0]) => apiService.createMetaAdset(params),
};

export default apiService; 
